import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import "./Department.scss";

import { getDepartmentsApi, createDepartmentApi } from "../../../../api/api";

import api from "../../../../api/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#0062FE",
  "#8B5CF6",
  "#22C55E",
  "#F97316",
  "#EF4444",
  "#06B6D4",
  "#F59E0B",
  "#9CA3AF",
];
function avatarColor(str = "") {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const COLOR_OPTIONS = [
  "#0062FE",
  "#8B5CF6",
  "#22C55E",
  "#F97316",
  "#EF4444",
  "#06B6D4",
  "#F59E0B",
  "#6B7280",
];

function initials(n = "") {
  return n
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const PAGE_SIZE = 8;

// ─── Lightweight toast ────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  }, []);
  return {
    toasts,
    toast: {
      success: (m) => add(m, "success"),
      error: (m) => add(m, "error"),
      info: (m) => add(m, "info"),
    },
  };
}
function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            background:
              t.type === "success"
                ? "#22C55E"
                : t.type === "error"
                  ? "#EF4444"
                  : "#0062FE",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            minWidth: 220,
          }}
        >
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const Ico = {
  Search: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Plus: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Table: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  ),
  Tree: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  ),
  Dots: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  ),
  Edit: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Users: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Archive: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  ),
  Restore: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-3.15" />
    </svg>
  ),
  Close: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevR: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ChevD: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  SortUp: (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  Building: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="15" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  ),
  Room: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Warn: (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Export: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Spinner: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

// ─── Row Dropdown ─────────────────────────────────────────────────────────────
function RowMenu({ dept, onEdit, onDelete, onRestore, onViewUsers }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const isActive = dept.status === "ACTIVE";

  return (
    <div className="dp-row-actions" ref={ref}>
      <button className="dp-menu-btn" onClick={() => setOpen((v) => !v)}>
        {Ico.Dots}
      </button>
      {open && (
        <div className="dp-dropdown">
          <button
            className="dp-dropdown__item"
            onClick={() => {
              onEdit(dept);
              setOpen(false);
            }}
          >
            {Ico.Edit} Edit Department
          </button>
          <button
            className="dp-dropdown__item"
            onClick={() => {
              onViewUsers(dept);
              setOpen(false);
            }}
          >
            {Ico.Users} View Users
          </button>
          <button
            className="dp-dropdown__item"
            onClick={() => {
              setOpen(false);
            }}
          >
            {Ico.Export} Export Members
          </button>
          {isActive ? (
            <button
              className="dp-dropdown__item dp-dropdown__item--danger dp-dropdown__item--divider"
              onClick={() => {
                onDelete(dept);
                setOpen(false);
              }}
            >
              {Ico.Archive} Archive
            </button>
          ) : (
            <button
              className="dp-dropdown__item dp-dropdown__item--divider"
              onClick={() => {
                onRestore(dept);
                setOpen(false);
              }}
            >
              {Ico.Restore} Restore
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Detail Modal (GET /departments/:id) ─────────────────────────────────────
function DeptDetailModal({ deptId, onClose }) {
  const [dept, setDept] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/departments/${deptId}`);
        setDept(res.data.department);
      } catch {
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [deptId]);

  return (
    <>
      <div className="dp-drawer-overlay" onClick={onClose} />
      <div className="dp-drawer" style={{ maxWidth: 460 }}>
        <div className="dp-drawer__header">
          <h3 className="dp-drawer__title">Department Detail</h3>
          <button className="dp-drawer__close" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>
        <div className="dp-drawer__body">
          {loading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 40 }}
            >
              <span className="dp-spin">{Ico.Spinner}</span>
            </div>
          ) : dept ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginBottom: 20,
                }}
              >
                <div
                  className="dp-dept-cell__icon"
                  style={{
                    background: avatarColor(dept.name) + "22",
                    color: avatarColor(dept.name),
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {Ico.Building}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17 }}>
                    {dept.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>
                    Code: {dept.code} · Created {formatDate(dept.createdAt)}
                  </div>
                  <span
                    className={`dp-badge dp-badge--${dept.status?.toLowerCase()}`}
                    style={{ marginTop: 4, display: "inline-block" }}
                  >
                    {dept.status}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                {[
                  {
                    label: "Users",
                    value: dept._count?.users ?? dept.users?.length ?? 0,
                  },
                  { label: "Rooms", value: dept._count?.rooms ?? 0 },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      flex: 1,
                      background: "#F9FAFB",
                      borderRadius: 8,
                      padding: "12px 16px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Users list */}
              {dept.users?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 10,
                    }}
                  >
                    Members
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {dept.users.map((u) => (
                      <div
                        key={u.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 0",
                          borderBottom: "1px solid #F3F4F6",
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: avatarColor(u.fullName),
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {initials(u.fullName)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>
                            {u.fullName}
                          </div>
                          <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                            {u.email} · {u.role?.name || u.role}
                          </div>
                        </div>
                        <span
                          className={`dp-badge dp-badge--${u.status?.toLowerCase()}`}
                        >
                          {u.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

// ─── Add / Edit Drawer ────────────────────────────────────────────────────────
// POST /departments  payload: { name, code }
// PATCH /departments/:id  payload: { name, code, status }
const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  status: "ACTIVE",
  color: "#0062FE",
  autoAddToRoom: false,
};

function DeptDrawer({ dept, departments, onClose, onSave, saving }) {
  const isEdit = !!dept?.id;
  const [form, setForm] = useState(() =>
    isEdit
      ? {
          ...EMPTY_FORM,
          name: dept.name || "",
          code: dept.code || "",
          status: dept.status || "ACTIVE",
          color: dept._color || "#0062FE",
        }
      : { ...EMPTY_FORM },
  );
  const [errors, setErrors] = useState({});

  // Auto-generate code from name
  useEffect(() => {
    if (!isEdit && form.name && !form.code) {
      setForm((p) => ({
        ...p,
        code: form.name
          .replace(/[^a-zA-Z0-9]/g, "")
          .toUpperCase()
          .slice(0, 6),
      }));
    }
  }, [form.name]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Department name is required";
    if (!form.code.trim()) e.code = "Department code is required";
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    // Payload for POST /departments or PATCH /departments/:id
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      ...(isEdit && { status: form.status }),
    };
    onSave(payload, dept?.id);
  };

  return (
    <>
      <div className="dp-drawer-overlay" onClick={onClose} />
      <div className="dp-drawer">
        <div className="dp-drawer__header">
          <div>
            <h3 className="dp-drawer__title">
              {isEdit ? "Edit Department" : "Add Department"}
            </h3>
            <p className="dp-drawer__subtitle">
              {isEdit ? `Editing: ${dept.name}` : "Create a new department"}
            </p>
          </div>
          <button className="dp-drawer__close" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>

        <div className="dp-drawer__body">
          <div className="dp-form-section">
            <span className="dp-form-section__label">Details</span>
            <div className="dp-form-row dp-form-row--full">
              <div className="dp-field">
                <label>
                  Department Name <span className="required">*</span>
                </label>
                <input
                  className={`dp-input${errors.name ? " error" : ""}`}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Operations Command"
                />
                {errors.name && (
                  <span className="dp-field-error">{errors.name}</span>
                )}
              </div>
            </div>
            <div className="dp-form-row">
              <div className="dp-field">
                <label>
                  Code <span className="required">*</span>
                </label>
                <input
                  className={`dp-input${errors.code ? " error" : ""}`}
                  value={form.code}
                  onChange={(e) => set("code", e.target.value.toUpperCase())}
                  placeholder="e.g. OPS"
                  maxLength={10}
                />
                {errors.code && (
                  <span className="dp-field-error">{errors.code}</span>
                )}
              </div>
              {isEdit && (
                <div className="dp-field">
                  <label>Status</label>
                  <select
                    className="dp-select"
                    value={form.status}
                    onChange={(e) => set("status", e.target.value)}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="dp-field">
              <label>Colour Tag</label>
              <div className="dp-color-row">
                {COLOR_OPTIONS.map((c) => (
                  <div
                    key={c}
                    className={`dp-color-swatch${form.color === c ? " selected" : ""}`}
                    style={{ background: c }}
                    onClick={() => set("color", c)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="dp-drawer__footer">
          <button
            className="dp-btn dp-btn--primary"
            style={{ flex: 1 }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="dp-spin" style={{ marginRight: 6 }}>
                  {Ico.Spinner}
                </span>
                Saving…
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Create Department"
            )}
          </button>
          <button
            className="dp-btn dp-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Archive/Delete Confirm Modal ─────────────────────────────────────────────
function ArchiveModal({ dept, onConfirm, onCancel, loading }) {
  const userCount = dept._count?.users ?? dept.userCount ?? 0;
  const hasUsers = userCount > 0;
  return (
    <div className="dp-modal-overlay">
      <div className="dp-modal">
        <div className="dp-modal__header">
          <div
            className="dp-modal__icon"
            style={{ background: "rgba(239,68,68,0.10)", color: "#EF4444" }}
          >
            {Ico.Warn}
          </div>
          <div>
            <h4 className="dp-modal__title">Archive "{dept.name}"?</h4>
            <p className="dp-modal__desc">
              {hasUsers
                ? `This department has ${userCount} active user${userCount > 1 ? "s" : ""}. Reassign them before archiving.`
                : "This department has no active users and can be safely archived."}
            </p>
          </div>
        </div>
        {hasUsers && (
          <div className="dp-modal__body">
            <div
              style={{
                background: "#FFF5F5",
                border: "1px solid #FECACA",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                color: "#B91C1C",
              }}
            >
              ⚠ Archive blocked — reassign all {userCount} user
              {userCount > 1 ? "s" : ""} first.
            </div>
          </div>
        )}
        <div className="dp-modal__footer">
          <button
            className="dp-btn dp-btn--secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="dp-btn dp-btn--danger"
            disabled={hasUsers || loading}
            onClick={onConfirm}
          >
            {loading ? "Archiving…" : "Archive Department"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tree Card View ───────────────────────────────────────────────────────────
function TreeView({ departments, onEdit, onDelete, onRestore, onViewUsers }) {
  const [expanded, setExpanded] = useState({});

  // Since the API doesn't return parentId, show a flat tree of all depts
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="dp-tree-cards">
      {departments.map((dept) => {
        const color = avatarColor(dept.name);
        const isOpen = expanded[dept.id];
        const userCount = dept._count?.users ?? dept.userCount ?? 0;

        return (
          <div key={dept.id} className="dp-tree-card">
            <div
              className="dp-tree-card__header"
              onClick={() => toggle(dept.id)}
            >
              <div
                className="dp-tree-card__icon"
                style={{ background: color + "18", color }}
              >
                {Ico.Building}
              </div>
              <div className="dp-tree-card__info">
                <div className="dp-tree-card__name">{dept.name}</div>
                <div className="dp-tree-card__meta">
                  Code: {dept.code} · Created {formatDate(dept.createdAt)}
                </div>
              </div>
              <div className="dp-tree-card__stats">
                <div className="dp-tree-card__stat">
                  <div className="dp-tree-card__stat-val" style={{ color }}>
                    {userCount}
                  </div>
                  <div className="dp-tree-card__stat-lbl">Users</div>
                </div>
                <span
                  className={`dp-badge dp-badge--${dept.status?.toLowerCase()}`}
                >
                  {dept.status}
                </span>
              </div>
              <div className={`dp-tree-card__chevron${isOpen ? " open" : ""}`}>
                {Ico.ChevD}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <RowMenu
                  dept={dept}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onViewUsers={onViewUsers}
                />
              </div>
            </div>
            {isOpen && (
              <div
                className="dp-tree-card__children"
                style={{ padding: "14px 16px" }}
              >
                <div style={{ fontSize: 13, color: "#6B7280" }}>
                  Dept ID:{" "}
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {dept.id}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>
                  {userCount} member{userCount !== 1 ? "s" : ""}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusF] = useState("All");
  const [view, setView] = useState("table");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const [drawer, setDrawer] = useState(null); // { mode, dept }
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [viewDeptId, setViewDeptId] = useState(null);

  const { toasts, toast } = useToast();

  // ── Fetch departments ────────────────────────────────────────────────────
  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDepartmentsApi();
      setDepartments(res.data.departments || []);
    } catch {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: departments.length,
      active: departments.filter((d) => d.status === "ACTIVE").length,
      inactive: departments.filter((d) => d.status !== "ACTIVE").length,
    }),
    [departments],
  );

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...departments];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.code || "").toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "All") {
      const val = statusFilter.toUpperCase();
      list = list.filter((d) => d.status === val);
    }
    list.sort((a, b) => {
      const aV = (a[sortKey] || "").toString().toLowerCase();
      const bV = (b[sortKey] || "").toString().toLowerCase();
      return sortDir === "asc" ? aV.localeCompare(bV) : bV.localeCompare(aV);
    });
    return list;
  }, [departments, search, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  // ── CRUD handlers ─────────────────────────────────────────────────────────

  // Create: POST /departments  { name, code }
  // Update: PATCH /departments/:id  { name, code, status }
  const handleSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        // PATCH /departments/:id
        const res = await api.patch(`/departments/${id}`, payload);
        const updated = res.data.department || {
          ...departments.find((d) => d.id === id),
          ...payload,
        };
        setDepartments((prev) =>
          prev.map((d) => (d.id === id ? { ...d, ...updated } : d)),
        );
        toast.success(`"${payload.name}" updated`);
      } else {
        // POST /departments
        await createDepartmentApi(payload);
        toast.success(`"${payload.name}" created`);
        await fetchDepartments();
      }
      setDrawer(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to save department";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Soft delete: DELETE /departments/:id
  const handleDelete = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await api.delete(`/departments/${archiveTarget.id}`);
      toast.success(`"${archiveTarget.name}" archived`);
      setDepartments((prev) =>
        prev.map((d) =>
          d.id === archiveTarget.id ? { ...d, status: "INACTIVE" } : d,
        ),
      );
      setArchiveTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to archive department";
      toast.error(msg);
    } finally {
      setArchiving(false);
    }
  };

  // Restore: PATCH /departments/:id  { status: "ACTIVE" }
  const handleRestore = async (dept) => {
    try {
      await api.patch(`/departments/${dept.id}`, { status: "ACTIVE" });
      setDepartments((prev) =>
        prev.map((d) => (d.id === dept.id ? { ...d, status: "ACTIVE" } : d)),
      );
      toast.success(`"${dept.name}" restored`);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to restore department";
      toast.error(msg);
    }
  };

  const openAdd = () => setDrawer({ mode: "add", dept: null });
  const openEdit = (dept) => setDrawer({ mode: "edit", dept });
  const closeDrawer = () => setDrawer(null);

  const SortTh = ({ col, label }) => (
    <th
      className={sortKey === col ? "sorted" : ""}
      onClick={() => handleSort(col)}
    >
      {label}
      <span className={`sort-icon${sortKey === col ? " active" : ""}`}>
        {Ico.SortUp}
      </span>
    </th>
  );

  return (
    <div className="dept-page">
      <ToastStack toasts={toasts} />

      {/* ── Header ── */}
      <div className="dp-header">
        <div className="dp-header__left">
          <span className="dp-header__label">User Management</span>
          <h1 className="dp-header__title">Departments</h1>
          <p className="dp-header__subtitle">
            Manage organisation structure and reporting groups
          </p>
        </div>
        <div className="dp-header__right">
          <button className="dp-btn dp-btn--secondary">
            {Ico.Export} Export
          </button>
          <button className="dp-btn dp-btn--primary" onClick={openAdd}>
            {Ico.Plus} Add Department
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="dp-kpi-grid">
        {[
          {
            label: "Total Departments",
            value: stats.total,
            color: "#0062FE",
            bg: "rgba(0,98,254,0.10)",
            icon: Ico.Building,
          },
          {
            label: "Active",
            value: stats.active,
            color: "#22C55E",
            bg: "rgba(34,197,94,0.10)",
            icon: Ico.Users,
          },
          {
            label: "Inactive / Archived",
            value: stats.inactive,
            color: "#9CA3AF",
            bg: "#F3F4F6",
            icon: Ico.Archive,
          },
        ].map((k) => (
          <div key={k.label} className="dp-kpi">
            <div
              className="dp-kpi__icon"
              style={{ background: k.bg, color: k.color }}
            >
              {k.icon}
            </div>
            <div className="dp-kpi__body">
              <div className="dp-kpi__value">{loading ? "—" : k.value}</div>
              <div className="dp-kpi__label">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="dp-toolbar">
        <div className="dp-search-wrap">
          {Ico.Search}
          <input
            className="dp-search"
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="dp-filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusF(e.target.value);
            setPage(1);
          }}
        >
          {["All", "Active", "Inactive"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <div className="dp-toolbar-right">
          <div className="dp-view-toggle">
            <button
              className={view === "table" ? "active" : ""}
              onClick={() => setView("table")}
              title="Table view"
            >
              {Ico.Table}
            </button>
            <button
              className={view === "tree" ? "active" : ""}
              onClick={() => setView("tree")}
              title="Tree view"
            >
              {Ico.Tree}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="dp-card">
          <div className="dp-empty">
            <span
              className="dp-spin"
              style={{ fontSize: 32, color: "#0062FE" }}
            >
              {Ico.Spinner}
            </span>
            <p style={{ marginTop: 12, color: "#6B7280" }}>
              Loading departments…
            </p>
          </div>
        </div>
      ) : view === "tree" ? (
        <div className="dp-card" style={{ overflow: "visible" }}>
          {filtered.length === 0 ? (
            <div className="dp-empty">
              {Ico.Building}
              <h4>No departments found</h4>
              <p>Try adjusting your filters.</p>
            </div>
          ) : (
            <TreeView
              departments={filtered}
              onEdit={openEdit}
              onDelete={setArchiveTarget}
              onRestore={handleRestore}
              onViewUsers={(dept) => setViewDeptId(dept.id)}
            />
          )}
        </div>
      ) : (
        <div className="dp-card">
          <div className="dp-table-wrap">
            {paginated.length === 0 ? (
              <div className="dp-empty">
                {Ico.Building}
                <h4>No departments found</h4>
                <p>Try adjusting your search or filter.</p>
                <button
                  className="dp-btn dp-btn--secondary"
                  onClick={() => {
                    setSearch("");
                    setStatusF("All");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <table className="dp-table">
                <thead>
                  <tr>
                    <SortTh col="name" label="Department" />
                    <SortTh col="code" label="Code" />
                    <SortTh col="status" label="Status" />
                    <SortTh col="createdAt" label="Created" />
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((dept) => {
                    const color = avatarColor(dept.name);
                    const userCount = dept._count?.users ?? dept.userCount ?? 0;
                    return (
                      <tr key={dept.id}>
                        <td>
                          <div className="dp-dept-cell">
                            <div
                              className="dp-dept-cell__icon"
                              style={{ background: color + "18", color }}
                            >
                              {Ico.Building}
                            </div>
                            <div>
                              <div className="dp-dept-cell__name">
                                {dept.name}
                              </div>
                              <div
                                className="dp-dept-cell__parent"
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: 11,
                                }}
                              >
                                {dept.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 12.5,
                              background: "#F3F4F6",
                              padding: "2px 8px",
                              borderRadius: 4,
                            }}
                          >
                            {dept.code}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`dp-badge dp-badge--${dept.status?.toLowerCase()}`}
                          >
                            {dept.status}
                          </span>
                        </td>
                        <td
                          style={{
                            fontSize: 12.5,
                            color: "#6B7280",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDate(dept.createdAt)}
                        </td>
                        <td>
                          <RowMenu
                            dept={dept}
                            onEdit={openEdit}
                            onDelete={setArchiveTarget}
                            onRestore={handleRestore}
                            onViewUsers={(d) => setViewDeptId(d.id)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ── */}
          <div className="dp-pagination">
            <div className="dp-pagination__info">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–
              {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}{" "}
              departments
            </div>
            <div className="dp-pagination__controls">
              <button
                className="dp-pagination__page"
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>
              <button
                className="dp-pagination__page"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {Ico.ChevR}
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <button
                    key={p}
                    className={`dp-pagination__page${page === p ? " active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                className="dp-pagination__page"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {Ico.ChevR}
              </button>
              <button
                className="dp-pagination__page"
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Drawer ── */}
      {drawer && (
        <DeptDrawer
          dept={drawer.dept}
          departments={departments}
          onClose={closeDrawer}
          onSave={handleSave}
          saving={saving}
        />
      )}

      {/* ── Archive Confirm ── */}
      {archiveTarget && (
        <ArchiveModal
          dept={archiveTarget}
          onConfirm={handleDelete}
          onCancel={() => setArchiveTarget(null)}
          loading={archiving}
        />
      )}

      {/* ── Detail / Users Modal ── */}
      {viewDeptId && (
        <DeptDetailModal
          deptId={viewDeptId}
          onClose={() => setViewDeptId(null)}
        />
      )}
    </div>
  );
};

export default Department;
